import React, { useRef } from "react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { App } from "../../types";
import AppCard from "./AppCard";

interface SortableAppGridProps {
  apps: App[];
  sortable: boolean;
  onReorder: (activeId: number, overId: number) => void;
  onToggleFavorite?: (appId: number) => void;
  /** Pass through to AppCard for user-defined external links (My Apps). */
  isUserCustomApp?: boolean;
  /** Extra per-card UI (e.g. edit/delete buttons) absolutely positioned over the card. */
  renderOverlay?: (app: App) => React.ReactNode;
}

const DragGuardContext = React.createContext<React.MutableRefObject<boolean> | null>(
  null,
);

interface AppCardSlotProps {
  app: App;
  index: number;
  isUserCustomApp?: boolean;
  onToggleFavorite?: (appId: number) => void;
  renderOverlay?: (app: App) => React.ReactNode;
}

const SortableAppCard: React.FC<AppCardSlotProps> = ({
  app,
  index,
  isUserCustomApp,
  onToggleFavorite,
  renderOverlay,
}) => {
  const dragGuard = React.useContext(DragGuardContext);
  const { listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: app.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : undefined,
    opacity: isDragging ? 0.85 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative group/card h-full"
      {...listeners}
      onClickCapture={(event) => {
        if (!dragGuard?.current) return;
        event.preventDefault();
        event.stopPropagation();
        dragGuard.current = false;
      }}
    >
      <AppCard
        app={app}
        index={index}
        isUserCustomApp={isUserCustomApp}
        hideTopActions={isUserCustomApp}
        isFavorite={app.isFavorite}
        isDraggable
        onToggleFavorite={onToggleFavorite ? () => onToggleFavorite(app.id) : undefined}
      />
      {renderOverlay?.(app)}
    </div>
  );
};

const StaticAppCard: React.FC<AppCardSlotProps> = ({
  app,
  index,
  isUserCustomApp,
  onToggleFavorite,
  renderOverlay,
}) => (
  <div className="relative group/card h-full">
    <AppCard
      app={app}
      index={index}
      isUserCustomApp={isUserCustomApp}
      hideTopActions={isUserCustomApp}
      isFavorite={app.isFavorite}
      onToggleFavorite={onToggleFavorite ? () => onToggleFavorite(app.id) : undefined}
    />
    {renderOverlay?.(app)}
  </div>
);

const SortableAppGrid: React.FC<SortableAppGridProps> = ({
  apps,
  sortable,
  onReorder,
  onToggleFavorite,
  isUserCustomApp,
  renderOverlay,
}) => {
  const draggedRef = useRef(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    window.setTimeout(() => {
      draggedRef.current = false;
    }, 0);

    if (!over || active.id === over.id) return;
    const activeId = Number(active.id);
    const overId = Number(over.id);
    if (!Number.isInteger(activeId) || !Number.isInteger(overId)) return;
    onReorder(activeId, overId);
  };

  const grid = (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
      {sortable
        ? apps.map((app, index) => (
            <SortableAppCard
              key={app.id}
              app={app}
              index={index}
              isUserCustomApp={isUserCustomApp}
              onToggleFavorite={onToggleFavorite}
              renderOverlay={renderOverlay}
            />
          ))
        : apps.map((app, index) => (
            <StaticAppCard
              key={app.id}
              app={app}
              index={index}
              isUserCustomApp={isUserCustomApp}
              onToggleFavorite={onToggleFavorite}
              renderOverlay={renderOverlay}
            />
          ))}
    </div>
  );

  if (!sortable) return grid;

  return (
    <DragGuardContext.Provider value={draggedRef}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={() => {
          draggedRef.current = true;
        }}
        onDragCancel={() => {
          window.setTimeout(() => {
            draggedRef.current = false;
          }, 0);
        }}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={apps.map((app) => app.id)} strategy={rectSortingStrategy}>
          {grid}
        </SortableContext>
      </DndContext>
    </DragGuardContext.Provider>
  );
};

export default SortableAppGrid;
